// The one interleave: front matter → chapters, with anchored matter beside the
// chapter it names → back matter.
//
// SHARED BECAUSE IT HAS TWO CALLERS OVER TWO DIFFERENT SOURCES. The reader
// orders units from the built CATALOG (`src/reader/reading-order.ts`); the admin
// console orders them from `BOOK_CONFIGS`, on purpose, so it works against a
// bare dev deployment with no corpus built. Those are different inputs to the
// same question, and two copies of the answer would let the editor's next/prev
// disagree with the reader's about where a foreword sits.
//
// Bare refs, not titled units: a title is the one thing config and catalog
// genuinely disagree about (config has none), so each caller attaches its own.

import type { MatterPlacement } from "./bookConfig";

/** One unit of a book, identified but not described. */
export type UnitRef =
  | { kind: "chapter"; number: number }
  | { kind: "matter"; slug: string };

/** The minimum this needs to know about a matter document. */
export interface PlacedMatter {
  slug: string;
  placement: MatterPlacement;
}

/**
 * Order a book's units. `chapters` arrives in the book's own ascending order
 * (both `BookConfig.chapters` and `CatalogBook.chapters` are); matter keeps its
 * declared order within any one position, which is how a volume's epilogue is
 * made to precede its afterword.
 *
 * MATTER ANCHORED TO A CHAPTER NOT IN `chapters` IS DROPPED. See
 * `readingOrder` for the full reasoning — the short version is that the
 * Odyssey's volume-three matter is configured against chapters that stay out
 * of the catalog until they're seeded, and a volume-three foreword with no
 * volume-three chapters behind it is dangling rather than misplaced.
 */
export function orderUnits(
  chapters: readonly number[],
  matter: readonly PlacedMatter[],
): UnitRef[] {
  const ref = (m: PlacedMatter): UnitRef => ({ kind: "matter", slug: m.slug });
  const at = (where: "front" | "back") =>
    matter.filter((m) => m.placement.at === where).map(ref);
  const anchored = (where: "before" | "after", chapter: number) =>
    matter
      .filter(
        (m) => m.placement.at === where && m.placement.chapter === chapter,
      )
      .map(ref);

  return [
    ...at("front"),
    ...chapters.flatMap((number): UnitRef[] => [
      ...anchored("before", number),
      { kind: "chapter", number },
      ...anchored("after", number),
    ]),
    ...at("back"),
  ];
}

/** The `/read/:book/:unit` and `/admin/:book/:unit` route segment for a unit. */
export function unitSegment(unit: UnitRef): string {
  return unit.kind === "chapter" ? String(unit.number) : unit.slug;
}

/**
 * Resolve an untrusted `:unit` route segment against an ordered unit list.
 * All-digits segments are chapter numbers, anything else is a matter slug; a
 * segment naming neither is `null`, never a throw. Both routes that take one
 * (`/read/:book/:unit`, `/admin/:book/:unit`) resolve it through here.
 *
 * `Number("")` is 0 and `Number("3x")` is NaN — neither is a chapter, and
 * matching against the ordered list rejects those, "0", "-1" and "1.5" alike
 * without a special case for any of them.
 *
 * Generic in the element type so a caller holding richer units (the reader's
 * titled `ReadingUnit`) gets its own type back rather than a bare ref.
 */
export function findUnit<T extends UnitRef>(
  units: readonly T[],
  raw: string,
): T | null {
  if (/^\d+$/.test(raw)) {
    const number = Number(raw);
    return (
      units.find((u) => u.kind === "chapter" && u.number === number) ?? null
    );
  }
  return units.find((u) => u.kind === "matter" && u.slug === raw) ?? null;
}
